import React, { type FunctionComponent, useState } from 'react'
import { CardBody, spacing, colors, RadioWithLabel } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { Input } from '../../../shared-styled-components/SharedStyledComponents'
import { TimePickerField } from './TimePickerField'
import { CronExpressionDisplay } from './CronExpressionDisplay'
import { convertHourlyToCron } from './cronUtils'

const StyledCardBody = styled(CardBody)({
  padding: '12px',
})

const FormGroup = styled.div({
  marginBottom: spacing.X16,
})

const InputGroup = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  marginTop: spacing.X8,
})

const Label = styled.span({
  color: colors.GRAYSCALE.X70,
})

type ScheduleMode = 'every' | 'specific'

interface Props {
  onScheduleChange?: (cronExpression: string) => void
  defaultSchedule: string
}

export const HourlySchedule: FunctionComponent<React.PropsWithChildren<Props>> = ({
  onScheduleChange,
  defaultSchedule,
}) => {
  const cronParts = defaultSchedule.split(' ')
  const initialMinutes = cronParts[0]
  const initialHours = cronParts[1]
  const initialTime = `${initialHours.padStart(2, '0')}:${initialMinutes.padStart(2, '0')}`

  const [mode, setMode] = useState<ScheduleMode>('specific')
  const [hours, setHours] = useState(initialHours)
  const [minutes, setMinutes] = useState(initialMinutes)
  const [specificTime, setSpecificTime] = useState(initialTime)
  const { cron, readable } = convertHourlyToCron(mode, hours, minutes, specificTime)

  const handleModeChange = (isEvery: boolean) => {
    const newMode = isEvery ? 'every' : 'specific'
    setMode(newMode)
    updateCronExpression(newMode)
  }

  const updateCronExpression = (currentMode: ScheduleMode = mode) => {
    if (!onScheduleChange) return

    if (currentMode === 'every') {
      onScheduleChange(`${minutes} */${hours} * * *`)
    } else {
      // Parse HH:mm to cron
      const [hh, mm] = specificTime.split(':')
      onScheduleChange(`${mm} ${hh} * * *`)
    }
  }

  return (
    <StyledCardBody>
      <FormGroup>
        <RadioWithLabel
          id="schedule-mode-every"
          value="every"
          checked={mode === 'every'}
          onChange={() => handleModeChange(true)}
          label="Run at a regular interval (every X hours and Y minutes)"
        />
        <RadioWithLabel
          id="schedule-mode-specific"
          value="specific"
          checked={mode === 'specific'}
          onChange={() => handleModeChange(false)}
          label="Run once daily at a specific time"
        />
      </FormGroup>

      {mode === 'every' ? (
        <InputGroup>
          <Input
            type="number"
            min="1"
            max="23"
            value={hours}
            onChange={e => {
              const newHours = e.target.value
              setHours(newHours)
              onScheduleChange?.(`${minutes} */${newHours} * * *`)
            }}
            style={{ width: '80px' }}
          />
          <Label>hours</Label>
          <Input
            type="number"
            min="0"
            max="59"
            value={minutes}
            onChange={e => {
              const newMinutes = e.target.value
              setMinutes(newMinutes)
              onScheduleChange?.(`${newMinutes} */${hours} * * *`)
            }}
            style={{ width: '80px' }}
          />
          <Label>minutes</Label>
        </InputGroup>
      ) : (
        <TimePickerField
          value={specificTime}
          onChange={time => {
            setSpecificTime(time)
            const [hh, mm] = time.split(':')
            onScheduleChange?.(`${mm} ${hh} * * *`)
          }}
          label="Run at:"
        />
      )}

      <CronExpressionDisplay cronExpression={cron} cronDescription={readable} />
    </StyledCardBody>
  )
}
